import {
  STUDIO_WORKSPACE_STATE_VERSION,
  createDefaultStudioWorkspaceState,
  sanitizeStudioWorkspaceState,
  type StudioWorkspaceState,
} from "./studioWorkspaceState";

export const STUDIO_WORKSPACE_STORAGE_KEY = "vetcan.studio.workspace";

export type StudioWorkspaceFallbackReason =
  | "none"
  | "missing"
  | "invalid_json"
  | "invalid_schema"
  | "storage_unavailable";

export type StudioWorkspaceLoadResult = {
  state: StudioWorkspaceState;
  savedAt: string | null;
  restored: boolean;
  fallbackReason: StudioWorkspaceFallbackReason;
};

type StudioWorkspaceStorageRecord = {
  version: typeof STUDIO_WORKSPACE_STATE_VERSION;
  savedAt: string;
  state: StudioWorkspaceState;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function localStorageAvailable() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function removeStoredWorkspace() {
  if (!localStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(STUDIO_WORKSPACE_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup failures and fall back to defaults.
  }
}

function parseStoredWorkspace(raw: string): StudioWorkspaceStorageRecord | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) return null;
    if (parsed.version !== STUDIO_WORKSPACE_STATE_VERSION) return null;
    if (typeof parsed.savedAt !== "string" || !parsed.savedAt.trim()) return null;
    if (!isRecord(parsed.state)) return null;

    return {
      version: STUDIO_WORKSPACE_STATE_VERSION,
      savedAt: parsed.savedAt,
      state: sanitizeStudioWorkspaceState(parsed.state),
    };
  } catch {
    return null;
  }
}

export function loadStudioWorkspaceStorage(): StudioWorkspaceLoadResult {
  const defaults = createDefaultStudioWorkspaceState();

  if (!localStorageAvailable()) {
    return {
      state: defaults,
      savedAt: null,
      restored: false,
      fallbackReason: "storage_unavailable",
    };
  }

  let raw: string | null = null;

  try {
    raw = localStorage.getItem(STUDIO_WORKSPACE_STORAGE_KEY);
  } catch {
    return {
      state: defaults,
      savedAt: null,
      restored: false,
      fallbackReason: "storage_unavailable",
    };
  }

  if (!raw) {
    return {
      state: defaults,
      savedAt: null,
      restored: false,
      fallbackReason: "missing",
    };
  }

  const parsed = parseStoredWorkspace(raw);
  if (!parsed) {
    removeStoredWorkspace();

    let fallbackReason: StudioWorkspaceFallbackReason = "invalid_schema";

    try {
      JSON.parse(raw);
    } catch {
      fallbackReason = "invalid_json";
    }

    return {
      state: defaults,
      savedAt: null,
      restored: false,
      fallbackReason,
    };
  }

  return {
    state: parsed.state,
    savedAt: parsed.savedAt,
    restored: true,
    fallbackReason: "none",
  };
}

export function saveStudioWorkspaceStorage(
  state: StudioWorkspaceState,
  savedAt = new Date().toISOString()
) {
  if (!localStorageAvailable()) {
    return {
      ok: false,
      savedAt: null,
    };
  }

  const record: StudioWorkspaceStorageRecord = {
    version: STUDIO_WORKSPACE_STATE_VERSION,
    savedAt,
    state: sanitizeStudioWorkspaceState(state),
  };

  try {
    localStorage.setItem(STUDIO_WORKSPACE_STORAGE_KEY, JSON.stringify(record));
    return {
      ok: true,
      savedAt,
    };
  } catch {
    return {
      ok: false,
      savedAt: null,
    };
  }
}

export function updateStudioWorkspaceStorage(
  updater: (current: StudioWorkspaceState) => StudioWorkspaceState
) {
  const current = loadStudioWorkspaceStorage().state;
  return saveStudioWorkspaceStorage(updater(current));
}

export function resetStudioWorkspaceStorage() {
  if (!localStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(STUDIO_WORKSPACE_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}
